import { BaseService } from './baseService'
import { getApiBaseUrl } from '@/config/environment'

function recordingUploadFilename(videoFile: Blob): string {
  const t = videoFile.type.toLowerCase()
  if (t.includes('mp4')) {
    return 'recording.mp4'
  }
  return 'recording.webm'
}

export interface Recording {
  id: number
  participant: number
  project: number | null
  type: 'image' | 'video' | 'live'
  storage_url: string
  fps: number | null
  duration_seconds: number | null
  note: string
  created_at: string
  updated_at: string
}

export interface RecordingTask {
  id: number
  project_participant: {
    id: number
    project: number
    participant: number
    participant_name: string
    participant_email: string
    project_name: string
    status: string
    assigned_at: string
  }
  task_type: 'recording_with_video' | 'recording_simple'
  reaction_video: string | null
  reaction_video_url: string | null
  note: string
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled'
  created_by: number
  created_by_name: string
  created_at: string
  updated_at: string
  completed_at: string | null
  recording_id?: number | null
  recording_data?: Recording | null
}

export interface RecordingSession {
  id: number
  project_participant: {
    id: number
    project: number
    participant: number
    participant_name: string
    participant_email: string
    project_name: string
    status: string
    assigned_at: string
  }
  session_type: 'recording_with_video' | 'recording_simple'
  reaction_video: string | null
  reaction_video_url: string | null
  note: string
  recording: number | null
  recording_data: any | null
  created_by: number
  created_by_name: string
  started_at: string | null
  ended_at: string | null
  status: 'created' | 'started' | 'recording' | 'completed' | 'cancelled'
  created_at: string
  updated_at: string
}

export interface CreateRecordingTaskRequest {
  project_participant_id: number
  task_type: 'recording_with_video' | 'recording_simple'
  reaction_video?: File | null
  note?: string
}

export interface CreateRecordingSessionRequest {
  project_participant_id: number
  session_type: 'recording_with_video' | 'recording_simple'
  reaction_video?: File | null
  note?: string
}

export interface UploadVideoRequest {
  participant_id: number
  project_id: number
  video_file: File
  note?: string
}

class RecordingService extends BaseService {
  constructor() {
    super(getApiBaseUrl())
  }

  async createRecordingTask(data: CreateRecordingTaskRequest): Promise<RecordingTask> {
    const formData = new FormData()
    formData.append('project_participant_id', data.project_participant_id.toString())
    formData.append('task_type', data.task_type)
    if (data.reaction_video) {
      formData.append('reaction_video', data.reaction_video)
    }
    if (data.note) {
      formData.append('note', data.note)
    }

    const token = this.getAuthToken()
    const url = this.urlBuilder.build('/recordings/tasks/')

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || errorData.detail || `HTTP error! status: ${response.status}`)
    }

    return await response.json()
  }

  async getRecordings(participantId?: number, projectId?: number): Promise<Recording[]> {
    const params = new URLSearchParams()
    if (participantId) params.append('participant_id', participantId.toString())
    if (projectId) params.append('project_id', projectId.toString())

    const queryString = params.toString()
    const endpoint = `/recordings/${queryString ? `?${queryString}` : ''}`
    const response = await this.request<Recording[]>(endpoint, { method: 'GET' })
    return response
  }

  async getRecordingTasks(participantId?: number, projectId?: number, projectParticipantId?: number): Promise<RecordingTask[]> {
    const params = new URLSearchParams()
    if (projectParticipantId) {
      params.append('project_participant_id', projectParticipantId.toString())
    } else {
      if (participantId) params.append('participant_id', participantId.toString())
      if (projectId) params.append('project_id', projectId.toString())
    }

    const queryString = params.toString()
    const endpoint = `/recordings/tasks${queryString ? `?${queryString}` : ''}`
    const response = await this.request<RecordingTask[]>(endpoint, { method: 'GET' })
    return response
  }

  async getRecordingTask(taskId: number): Promise<RecordingTask> {
    const response = await this.request<RecordingTask>(`/recordings/tasks/${taskId}/`, { method: 'GET' })
    return response
  }

  async updateRecordingTaskStatus(taskId: number, status: string): Promise<RecordingTask> {
    const response = await this.request<RecordingTask>(`/recordings/tasks/${taskId}/`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    })
    return response
  }

  async completeRecordingTask(taskId: number, videoFile: Blob): Promise<{ task: RecordingTask; recording_id: number; analysis_job_id: number; message: string }> {
    const formData = new FormData()
    formData.append('video_file', videoFile, recordingUploadFilename(videoFile))

    const token = this.getAuthToken()
    const url = this.urlBuilder.build(`/recordings/tasks/${taskId}/complete/`)

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || errorData.detail || `HTTP error! status: ${response.status}`)
    }

    return await response.json()
  }

  async deleteRecordingTask(taskId: number): Promise<void> {
    await this.request(`/recordings/tasks/${taskId}/`, { method: 'DELETE' })
  }

  async createRecordingSession(data: CreateRecordingSessionRequest): Promise<RecordingSession> {
    const formData = new FormData()
    formData.append('project_participant_id', data.project_participant_id.toString())
    formData.append('session_type', data.session_type)
    if (data.reaction_video) {
      formData.append('reaction_video', data.reaction_video)
    }
    if (data.note) {
      formData.append('note', data.note)
    }

    const token = this.getAuthToken()
    const url = this.urlBuilder.build('/recordings/sessions/')

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || errorData.detail || `HTTP error! status: ${response.status}`)
    }

    return await response.json()
  }

  async getRecordingSessions(participantId?: number, projectId?: number, projectParticipantId?: number): Promise<RecordingSession[]> {
    const params = new URLSearchParams()
    if (projectParticipantId) {
      params.append('project_participant_id', projectParticipantId.toString())
    } else {
      if (participantId) params.append('participant_id', participantId.toString())
      if (projectId) params.append('project_id', projectId.toString())
    }

    const queryString = params.toString()
    const endpoint = `/recordings/sessions${queryString ? `?${queryString}` : ''}`
    const response = await this.request<RecordingSession[]>(endpoint, { method: 'GET' })
    return response
  }

  async getRecordingSession(sessionId: number): Promise<RecordingSession> {
    const response = await this.request<RecordingSession>(`/recordings/sessions/${sessionId}/`, { method: 'GET' })
    return response
  }

  async startRecordingSession(sessionId: number): Promise<RecordingSession> {
    const response = await this.request<RecordingSession>(`/recordings/sessions/${sessionId}/start/`, {
      method: 'POST',
    })
    return response
  }

  async stopRecordingSession(
    sessionId: number, 
    options?: {
      storageUrl?: string
      fps?: number
      durationSeconds?: number
      videoFile?: Blob
    }
  ): Promise<RecordingSession> {
    const { storageUrl, fps, durationSeconds, videoFile } = options || {}
    const formData = new FormData()
    
    if (videoFile) {
      formData.append('video_file', videoFile, recordingUploadFilename(videoFile))
    } else if (storageUrl) {
      formData.append('storage_url', storageUrl)
    }
    
    if (fps !== undefined) formData.append('fps', fps.toString())
    if (durationSeconds !== undefined) formData.append('duration_seconds', durationSeconds.toString())

    const headers: HeadersInit = {}
    const config: RequestInit = {
      method: 'POST',
      headers,
      body: formData,
    }

    return await this.requestWithRefresh<RecordingSession>(`/recordings/sessions/${sessionId}/stop/`, config)
  }

  async uploadVideoForAnalysis(data: UploadVideoRequest): Promise<{ recording: any; analysis_job_id: number; message: string }> {
    const formData = new FormData()
    formData.append('participant_id', data.participant_id.toString())
    formData.append('project_id', data.project_id.toString())
    formData.append('video_file', data.video_file)
    if (data.note) {
      formData.append('note', data.note)
    }

    const token = this.getAuthToken()
    const url = this.urlBuilder.build('/recordings/upload-video/')

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || errorData.detail || `HTTP error! status: ${response.status}`)
    }

    return await response.json()
  }

  async startAnalysis(recordingId: number): Promise<{ id: number; status: string; progress: number }> {
    const response = await this.request<{ id: number; status: string; progress: number }>(
      `/analysis/recording/${recordingId}/analyze/`,
      { method: 'POST' }
    )
    return response
  }

  async getAnalysisJobStatus(jobId: number): Promise<{ id: number; status: string; progress: number }> {
    const response = await this.request<{ id: number; status: string; progress: number }>(
      `/analysis/job/${jobId}/status/`,
      { method: 'GET' }
    )
    return response
  }

  async getAnalysisResults(recordingId: number): Promise<{
    job: { id: number; status: string; summary: any }
    frame_results: Array<{
      id: number
      frame_index: number
      timestamp_ms: number
      emotions_vector: Record<string, number>
      confidence: number
    }>
    summary: any
  }> {
    const response = await this.request<{
      job: { id: number; status: string; summary: any }
      frame_results: Array<{
        id: number
        frame_index: number
        timestamp_ms: number
        emotions_vector: Record<string, number>
        confidence: number
      }>
      summary: any
    }>(`/analysis/recording/${recordingId}/results/`, { method: 'GET' })
    return response
  }

  async getRecordingAnalysisStatus(recordingId: number): Promise<{ has_analysis: boolean; job_id?: number; status?: string; progress?: number; has_eye_tracking?: boolean } | null> {
    try {
      const response = await this.request<{ has_analysis: boolean; job_id?: number; status?: string; progress?: number; has_eye_tracking?: boolean }>(
        `/analysis/recording/${recordingId}/status/`,
        { method: 'GET' }
      )
      return response
    } catch {
      return null
    }
  }
}

export const recordingService = new RecordingService()

